import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CookieService } from 'ngx-cookie';
import { catchError, debounceTime, finalize, pairwise, startWith, tap } from 'rxjs/operators';
import { RegisterForm } from 'src/app/shared/forms/auth/register.form';
import { fadeConsents, fadePasswordHelp } from 'src/app/shared/utils/registration.animations';
import { REFERRAL_CODE } from '../../../core/models/cookies.model';
import { AnalyticsService } from '../../../core/services/analytics-service';
import { SavedUrlService } from '../../../core/services/saved-url.service';
import { AuthStoreService } from '../../../shared/services/auth/auth-store.service';
import { RegistrationService } from '../../../shared/services/auth/registration.service';
import { NAME_PATTERN } from '../../../shared/utils/validation.utils';
import { AppConfig } from '../../../core/services/app-config.service';
import { ConfigurationState } from '../../../core/enums/configuration-state.enum';
import { MatDialog } from '@angular/material/dialog';
import { FormGroup } from '@angular/forms';
import { RegistrationStoreService } from '../../../shared/services/auth/registration-store.service';
import { Regulation } from '../../../shared/models/regulation.model';
import { HandsetService } from '../../../core/services/handset.service';
import { PhoneFormatPipe } from '../../../shared/pipes/phone-format.pipe';
import { throwError } from 'rxjs';

@Component({
standalone: false,
  animations: [fadePasswordHelp, fadeConsents],
  selector: 'app-registration',
  styleUrls: ['./registration.component.scss'],
  templateUrl: './registration.component.html',
})
export class RegistrationComponent implements OnInit {
  public stepForm!: FormGroup;
  public form: RegisterForm;
  public referralCodeInputVisible = false;
  public showPasswordHelper = false;
  public showConsent = false;
  public loading: boolean;
  public errors = [];
  public namePattern = NAME_PATTERN;
  public ConfigurationState = ConfigurationState;
  public regulations: Regulation[];

  public errorMessages = {
    INVALID_REFERRAL_CODE: 'Kod polecający utracił ważność lub jest niepoprawny.',
    USER_NAME_ALREADY_IN_USE: 'Adres e-mail jest już zajęty.',
    unknown: 'Wystąpił nieznany błąd. Spróbuj ponownie później.',
  };

  public formName = 'registration';

  constructor(
    private cookie: CookieService,
    private route: ActivatedRoute,
    public handsetService: HandsetService,
    private authStore: AuthStoreService,
    private analytics: AnalyticsService,
    private router: Router,
    private savedUrl: SavedUrlService,
    private registrationService: RegistrationService,
    private dialog: MatDialog,
    public appConfig: AppConfig,
    private registrationStore: RegistrationStoreService,
    private activatedRoute: ActivatedRoute,
  ) {
  }

  public ngOnInit(): void {
    this.registrationService.backToFirstStep();
    this.activatedRoute.data.subscribe((data: { regulations: Regulation[] }) => {
      this.regulations = data.regulations;
    });

    this.form = new RegisterForm();

    const { queryParamMap } = this.route.snapshot;
    this.form.email.setValue(this.preventUndefinedAndNullSetFormValue(queryParamMap.get('email')));
    this.form.utms = this.registrationService.addToCookiesAndGetUtmJson();


    this.subscribeToPasswordChanges();
    this.subscribeToReferralCodeChanges();

    this.handleReferralCode(queryParamMap.get('referralCode'));

    if (queryParamMap.has('email') && queryParamMap.has('regulations') && queryParamMap.has('code')) {
      this.registrationService.goToEmailVerification(queryParamMap.get('email'),
        queryParamMap.get('code'),
        queryParamMap.getAll('regulations'));
    } else {
      this.registrationService.backToFirstStep();
    }
  }

  preventUndefinedAndNullSetFormValue(value): string {
    if (!value || value === 'undefined') {
      return null;
    }
    return value;
  }

  private removeError(code: string): void {
    this.errors.forEach((item, index) => {
      if (item === code) {
        this.errors.splice(index, 1);
      }
    });
  }


  public changeAllConsentsState(checked: boolean): void {
    this.form.changeAllConsentsState(checked);
  }

  public changeAllMarketingConsentsState(checked: boolean): void {
    this.form.marketingConsents.changeAllMarketingConsentsState(checked);
  }

  public onSubmit(): void {
    this.form.tosAccepted.markAsTouched();
    this.form.controls.marketingConsents.markAllAsTouched();

    if (this.form.valid) {
      this.loading = true;

      const registerData = this.form.value;
      registerData.utms = this.form.utms;


      this.registrationService
        .register(registerData)
        .pipe(
          catchError((errors) => {
            this.errors = errors?.error?.errorCodes;
            return throwError(() => errors);
          }),
          finalize(() => {
            this.loading = false;
          }),
        )
        .subscribe(() => {
          this.registrationService.clearUtm();
          this.registrationService.addReferralCodeToCookie(registerData.referralCode);

          this.router.navigateByUrl(this.savedUrl.url);
        });
    }
  }

  private trackAnalyticsEvents(): void {
    this.registrationService.trackRegistrationEvent(
      'Customer Email Registration Completed',
      this.form.referralCode.value,
    );
    this.analytics.trackLogin();
    this.analytics.trackWpEventIfEnabled('Register');
  }


  private subscribeToPasswordChanges(): void {
    this.form.password.statusChanges.pipe(debounceTime(200)).subscribe((status) => {
      if (status === 'VALID') {
        this.showConsent = true;
        this.showPasswordHelper = false;
      } else {
        this.showPasswordHelper = true;
      }
    });
  }

  private subscribeToReferralCodeChanges(): void {
    this.form.referralCode.valueChanges
      .pipe(
        startWith(''),
        pairwise(),
        tap(([prev, current]) => {
          this.removeError('INVALID_REFERRAL_CODE');

          if (!prev && !!current && this.form.tosAccepted.value) {
            this.form.tosAccepted.setValue(false);
          }

          if (current) {
            this.form.marketingConsents.requireAllAndSetFalse();
          } else {
            this.form.marketingConsents.removeRequireAll();
          }
        }),
      )
      .subscribe();
  }

  private handleReferralCode(referralCode: string): void {
    referralCode = referralCode || this.cookie.get(REFERRAL_CODE);
    this.cookie.put(REFERRAL_CODE, referralCode);
    this.form.referralCode.setValue(referralCode);

    if (referralCode) {
      this.referralCodeInputVisible = true;
    }
  }

  private registerRedirectionWithToken(): void {

  }


  public get activeStep(): number {
    return this.registrationStore.state;
  }

  public get headerText(): string {
    switch (this.activeStep) {
      case 1:
        return 'Witaj w Panelu Payka';
      case 2:
        return 'Zweryfikuj adres email';
      case 3:
        return 'Twój numer telefonu';
      case 4:
        return 'Zweryfikuj nr telefonu';
      case 5:
        return 'Ustaw hasło';
    }
  }

  public get smallHeaderText(): string {
    switch (this.activeStep) {
      case 1:
        return 'Zarejestruj się';
      case 2:
        return 'Wpisz poniżej kod weryfikacyjny, który wysłaliśmy na  adres ';
      case 3:
        return 'Wpisz poniżej swój numer telefonu, którym będziesz potwierdzał działania w Panelu Payka.';
      case 4:
        return 'Wpisz poniżej kod SMS, który wysłaliśmy na numer ';
      case 5:
        return 'Ustaw swoje hasło do Panelu Payka.';
    }
  }

  public get emailText(): string {
    return this.form.get('addEmail').get('email').value;
  }

  public get phoneText(): string {
    const phonePipe = new PhoneFormatPipe();
    return phonePipe.transform(this.form.get('addPhone').get('phone').value);
  }

  firstStepClass() {
    if (this.activeStep === 1) {
      return 'right-content';
    }
    return 'center-content';
  }
}
